import * as React from 'react';
import {
    OutputReferenceHelper, IdHelper, ModalHelper, QueryBuilderHelper, IConditionConfig, IQueryBuilderRule,
    IValidateExpressionResponse, EOutputLookupContext
} from '../../helpers';
import { ContentEditableTinyMCE, NoReferenceableOutputsMessage } from '../';
import { VISIBILITY_EXPRESSION_PROPERTY_NAME } from '../../constants';
import { IDropdownOption, Label, Toggle, ButtonType, Button, Spinner, SpinnerType, MessageBar, MessageBarType } from 'office-ui-fabric-react';
import '../../plugins/output-reference/plugin';
declare var tinymce;

interface IProps {
    id: string;
    propertyOptions: IDropdownOption[];
    defaultOption?: IDropdownOption;
    queryBuilderConfig: IConditionConfig;
    label?: string;
    outputLookupContext: EOutputLookupContext;
    onChange: (queryBuilderConfig: IConditionConfig) => void;
}

export class QueryBuilder extends React.Component<IProps, any> {

    private helper: QueryBuilderHelper;

    constructor(props: any) {
        super(props);
        let { id, propertyOptions, outputLookupContext, onChange } = this.props;
        this.helper = new QueryBuilderHelper(id, propertyOptions, outputLookupContext, onChange);
        this.state = { isLoading: false };
    }

    private switchToAdvancedMode() {
        let { queryBuilderConfig, onChange } = this.props;

        //put into loading mode
        queryBuilderConfig.isLoading = true;
        onChange(queryBuilderConfig);

        //convert query builder config
        this.helper.convertToAdvancedMode().then((newConfig: IConditionConfig) => {
            //cleanup tinymce
            this.helper.cleanup();
            //additional configuration
            newConfig.isLoading = false;
            newConfig.isNotValidExpression = false;
            newConfig.validationMessages = null;
            //fire onChange
            this.props.onChange(newConfig);
        });

    }

    private switchFromAdvancedMode() {
        let { queryBuilderConfig, onChange } = this.props;

        //put into loading mode
        queryBuilderConfig.isLoading = true;
        onChange(queryBuilderConfig);

        //convert query builder config
        let expression = this.props.queryBuilderConfig.AdvancedConfig ?
            JSON.parse(this.props.queryBuilderConfig.AdvancedConfig).text : '';

        this.helper.convertFromAdvancedMode(expression).then((newConfig: IConditionConfig) => {
            //take out of loading mode            
            newConfig.isLoading = false;
            this.helper.setLoadQueryBuilderOnUpdate(true);
            //fire onChange
            this.props.onChange(newConfig);
        });
    }

    private validateAdvancedExpression() {
        let { queryBuilderConfig, onChange } = this.props;

        let expression = this.props.queryBuilderConfig.AdvancedConfig ?
            JSON.parse(this.props.queryBuilderConfig.AdvancedConfig).text : '';

        this.helper.validateExpression(expression).then((validateExpressionResponse: IValidateExpressionResponse) => {
            queryBuilderConfig.isValidating = false;
            queryBuilderConfig.isNotValidExpression = !validateExpressionResponse.isExpressionValid;
            queryBuilderConfig.validationMessages = !validateExpressionResponse.isExpressionValid ?
                validateExpressionResponse.validationMessages : null;
            onChange(queryBuilderConfig);
        });

    }

    public componentDidMount() {
        let { queryBuilderConfig, defaultOption, label } = this.props;
        if (this.props.propertyOptions.length && !queryBuilderConfig.AdvancedMode) {
            this.helper.initialiseQueryBuilder(label === VISIBILITY_EXPRESSION_PROPERTY_NAME, defaultOption);
            this.helper.applyQueryBuilderConfig(queryBuilderConfig, defaultOption);
        }
    }

    public componentDidUpdate(prevProps: IProps) {
        let { queryBuilderConfig, label } = this.props;

        if (!queryBuilderConfig.AdvancedMode && this.helper.getLoadQueryBuilderOnUpdate() && this.props.propertyOptions.length) {
            //initialise query builder
            this.helper.initialiseQueryBuilder(label === VISIBILITY_EXPRESSION_PROPERTY_NAME);

            //apply query builder config
            this.helper.applyQueryBuilderConfig(queryBuilderConfig);

            this.helper.setLoadQueryBuilderOnUpdate(false);
        }
        else if (this.helper.getForceApplyConfig()) {
            //apply query builder config
            this.helper.applyQueryBuilderConfig(queryBuilderConfig);
            //set force apply back to false
            this.helper.setForceApplyConfig(false);
        }
    }

    public componentWillUnmount() {

        if (this.props.propertyOptions.length && this.props.queryBuilderConfig && !this.props.queryBuilderConfig.AdvancedMode) {
            this.helper.cleanup(true);
        }

    }

    public render() {
        let { id, label, queryBuilderConfig, onChange, propertyOptions, outputLookupContext } = this.props;

        if (!this.props.propertyOptions.length) {
            return <div>
                {label ? <Label>{label}</Label> : null}
                <NoReferenceableOutputsMessage />
            </div>
        }

        let canSaveQueryEditor = QueryBuilderHelper.canSaveQueryEditor();
        let hasJourneyBeenSaved = QueryBuilderHelper.hasJourneyBeenSaved();
        let advancedModeToggleDisabled =
            (!queryBuilderConfig.AdvancedMode && !canSaveQueryEditor) ||
            !hasJourneyBeenSaved ||
            (queryBuilderConfig.AdvancedMode && queryBuilderConfig.isValidating) ||
            (queryBuilderConfig.AdvancedMode && queryBuilderConfig.isNotValidExpression)
            ;

        return (
            <div id={"query-builder-wrapper-" + id} className='query-builder-wrapper'>
                {queryBuilderConfig.isLoading ?
                    <div className="query-builder-loading-overlay">
                        <Spinner type={SpinnerType.large} />
                    </div> :
                    null
                }
                {label ? <Label>{label}</Label> : null}
                <Toggle label={'Advanced Mode' + (hasJourneyBeenSaved ? '' : ' (the journey must be saved before advanced mode can be used)')}
                    className='query-builder-advanced-mode-toggle'
                    disabled={advancedModeToggleDisabled}
                    checked={queryBuilderConfig.AdvancedMode}
                    onChanged={(val) => {
                        if (!advancedModeToggleDisabled) {
                            if (val) { this.switchToAdvancedMode(); }
                            else { this.switchFromAdvancedMode(); }
                        }
                    }}
                />
                {queryBuilderConfig.AdvancedMode ?
                    <div className="query-builder-advanced-mode-wrapper">
                        <ContentEditableTinyMCE
                            label={null}
                            value={queryBuilderConfig.AdvancedConfig || ''}
                            outputOptions={propertyOptions || []}
                            richText={false}
                            onBlur={(val) => {
                                let newConfig = queryBuilderConfig;
                                newConfig.AdvancedConfig = val;
                                newConfig.isValidating = true;
                                this.props.onChange(newConfig);
                                this.validateAdvancedExpression();
                            }}
                            outputLookupContext={outputLookupContext}
                            required={false}
                            readonly={false}
                        />
                        {queryBuilderConfig.isValidating &&
                            <Label className='query-builder-validating'>{'Validating...'}</Label>}
                        {!queryBuilderConfig.isValidating && queryBuilderConfig.isNotValidExpression === false && queryBuilderConfig.AdvancedConfig !== "{\"text\":\"\",\"markup\":\"\"}" &&
                            <Label className='query-builder-valid-expression'>{'Expression is valid'}</Label>}
                        {!queryBuilderConfig.isValidating && queryBuilderConfig.isNotValidExpression === true &&
                            <MessageBar className='query-builder-error-display' messageBarType={MessageBarType.error} >
                                <Label className='query-builder-not-valid-expression'>{'Expression is not valid'}</Label>
                                {queryBuilderConfig.validationMessages && queryBuilderConfig.validationMessages.length &&
                                    <ul className='error-list'>
                                        {queryBuilderConfig.validationMessages.map((m, i) => {
                                            return <li key={'query-builder-error-' + i} className='error-message-details'>
                                                {m}</li>;
                                        })}
                                    </ul>
                                }
                            </MessageBar>
                        }
                    </div> :
                    <div id={id} className="content-editable-wrapper"></div>

                }
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./source/ts/components/query-builder/query-builder.tsx